/** The four outcomes a toast reports. Drives colour, icon, role and duration. */
export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

/**
 * `exiting` is a real state rather than an immediate removal: the card animates
 * out, then removes itself. Keeping it in the list is what lets the stack
 * settle into the gap instead of snapping.
 */
export type ToastStatus = 'visible' | 'exiting';

export interface ToastAction {
  /** Verb, matching the control that produced the toast — "Undo", "Retry". */
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  /** Second line. Truncated to one line, so put the detail, not the outcome. */
  description?: string;
  action?: ToastAction;
  /**
   * Milliseconds on screen, or `null` to stay until dismissed. Omit to take the
   * variant default. Pass an explicit value when the message needs longer than
   * a glance — see the background-import notice in AddEditStock.
   */
  duration?: number | null;
  /**
   * Collapse key. Showing a toast whose key matches one already on screen
   * replaces it in place and restarts its timer instead of stacking a second
   * card. Use a stable key for a progressing operation (an export moving from
   * "in progress" to "done") so the user follows one card.
   */
  dedupeKey?: string;
  /**
   * Opt out of the default collapse. By default two toasts with identical
   * variant, title and description collapse into one — which is what stops a
   * double-clicked save or a repeated barcode scan from filling the stack.
   */
  allowDuplicate?: boolean;
  /**
   * Offer a copy button, so the message can be pasted to an administrator.
   * Defaults to on for errors — those are the ones that get escalated — and off
   * for everything else. Set it explicitly to override either way.
   */
  copyable?: boolean;
}

export interface ToastInput extends ToastOptions {
  variant: ToastVariant;
  /** The outcome, in the words the control used: "Changes saved", not "Success". */
  title: string;
}

export interface Toast extends ToastOptions {
  id: number;
  variant: ToastVariant;
  title: string;
  duration: number | null;
  dedupeKey: string;
  copyable: boolean;
  status: ToastStatus;
  /**
   * Bumped each time a duplicate collapses onto this toast. The card keys its
   * progress bar on it, so a collapse visibly restarts the countdown.
   */
  sequence: number;
}

/**
 * House durations. Longer for the variants that carry something to read and for
 * anything with an action — a 10-second Undo is a deadline, and 4 seconds is
 * not enough to notice it, read it and reach it.
 */
export const TOAST_DURATION = {
  short: 4000,
  medium: 6000,
  long: 8000,
  extended: 10000,
  /** Stays until dismissed. The card drops its progress bar and gains a close button. */
  sticky: null,
} as const;

/** How long the exit animation runs before the record is dropped. */
export const TOAST_EXIT_MS = 180;

/** Cards rendered at once. Anything beyond this waits its turn. */
export const TOAST_VISIBLE_LIMIT = 3;

/** Hard backstop so a viewport-less context (a unit test) can't grow unbounded. */
export const TOAST_QUEUE_LIMIT = 50;
